import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Megaphone, Globe, Building2 } from "lucide-react";
import { toast } from "sonner";
import { CAMPAIGN_TYPE_LABEL, label } from "@/lib/labels";

export default function Campaigns() {
  const { t, user } = useApp();
  const [items, setItems] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", message: "", target_category: "all",
    target_hospital_id: "all", type: "campaign", video_url: "",
  });

  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    const r = await axios.get(`${API}/campaigns`);
    setItems(r.data);
    if (isSuperAdmin) {
      try {
        const h = await axios.get(`${API}/hospitals`);
        setHospitals(h.data || []);
      } catch {}
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        message: form.message,
        type: form.type,
        target_category: form.target_category === "all" ? null : form.target_category,
        target_hospital_id: form.target_hospital_id === "all" ? null : form.target_hospital_id,
        video_url: form.type === "ad" ? (form.video_url || "") : "",
      };
      const r = await axios.post(`${API}/campaigns`, payload);
      toast.success(`Campagne envoyée à ${r.data.sent_count} patient(s) dans ${r.data.hospitals_count || 1} hôpital/aux (MOCK SMS)`);
      setOpen(false);
      setForm({ title: "", message: "", target_category: "all", target_hospital_id: "all", type: "campaign", video_url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const fmt = (d) => new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

  // Convert YouTube/Vimeo URLs into embed URLs; return "" if unsupported
  const toEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
      if (host.endsWith("youtube.com")) {
        const v = u.searchParams.get("v");
        if (v) return `https://www.youtube.com/embed/${v}`;
        if (u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
      }
      if (host.endsWith("vimeo.com")) {
        const seg = u.pathname.split("/").filter(Boolean)[0];
        if (seg && /^\d+$/.test(seg)) return `https://player.vimeo.com/video/${seg}`;
      }
      return "";
    } catch { return ""; }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-[Outfit] font-bold inline-flex items-center gap-3">
            <Megaphone className="w-7 h-7 text-[#D96C4A]"/>
            {isSuperAdmin ? "Campagnes nationales" : t.dash.campaigns}
          </h1>
          <p className="text-[#5C6B61] mt-1 text-sm">
            {isSuperAdmin
              ? "Diffusion de messages de santé publique gérée par l'équipe KWIKILA Care"
              : "Campagnes envoyées par KWIKILA Care · Consultable uniquement"}
          </p>
        </div>
        {isSuperAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white" data-testid="add-campaign-btn">
              <Plus className="w-4 h-4 mr-2"/> Nouvelle campagne
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle campagne santé</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="campaign-form">
              <div><Label>Titre</Label><Input value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} required data-testid="campaign-title"/></div>
              <div><Label>Message SMS</Label><Textarea value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} required rows={4} maxLength={300} data-testid="campaign-message"/>
                <div className="text-xs text-[#5C6B61] mt-1">{form.message.length}/300 caractères</div>
              </div>
              <div>
                <Label>Hôpital cible</Label>
                <Select value={form.target_hospital_id} onValueChange={(v)=>setForm({...form, target_hospital_id: v})}>
                  <SelectTrigger data-testid="campaign-hospital"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌍 Tous les hôpitaux (national)</SelectItem>
                    {hospitals.filter(h => h.status === "active").map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cible patients</Label>
                  <Select value={form.target_category} onValueChange={(v)=>setForm({...form, target_category: v})}>
                    <SelectTrigger data-testid="campaign-target"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="pregnant">Femmes enceintes</SelectItem>
                      <SelectItem value="child">Enfants 0-3 ans</SelectItem>
                      <SelectItem value="elderly">Personnes âgées</SelectItem>
                      <SelectItem value="general">Adultes / général</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
                    <SelectTrigger data-testid="campaign-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="campaign">Campagne santé</SelectItem>
                      <SelectItem value="advice">Conseil santé</SelectItem>
                      <SelectItem value="ad">Annonce (avec vidéo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.type === "ad" && (
                <div>
                  <Label>URL vidéo (YouTube ou Vimeo)</Label>
                  <Input value={form.video_url} onChange={(e)=>setForm({...form, video_url: e.target.value})} placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..." data-testid="campaign-video"/>
                  {form.video_url && toEmbedUrl(form.video_url) && (
                    <div className="mt-2 aspect-video w-full rounded-lg overflow-hidden border border-[#E5EBE7] bg-black">
                      <iframe
                        src={toEmbedUrl(form.video_url)}
                        title="Aperçu vidéo campagne"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                  )}
                  {form.video_url && !toEmbedUrl(form.video_url) && (
                    <div className="mt-1 text-xs text-[#C62828]">URL non supportée. Utilisez un lien YouTube ou Vimeo.</div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-[#D96C4A] hover:bg-[#B85A3D]" data-testid="campaign-save">Envoyer la campagne</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.length === 0 ? (
          <div className="md:col-span-2 bg-white p-12 text-center text-[#5C6B61] rounded-xl border border-[#E5EBE7]" data-testid="empty-campaigns">
            Aucune campagne pour le moment
          </div>
        ) : items.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-[#E5EBE7] p-6 hover:shadow-md transition" data-testid={`campaign-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D96C4A]/10 grid place-items-center text-[#D96C4A]">
                <Megaphone className="w-5 h-5"/>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-[Outfit] font-semibold text-lg">{c.title}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631]">{label(CAMPAIGN_TYPE_LABEL, c.type)}</span>
                </div>
                <p className="mt-2 text-sm text-[#5C6B61]">{c.message}</p>
                {c.type === "ad" && c.video_url && toEmbedUrl(c.video_url) && (
                  <div className="mt-3 aspect-video w-full rounded-lg overflow-hidden border border-[#E5EBE7] bg-black" data-testid={`campaign-video-${c.id}`}>
                    <iframe
                      src={toEmbedUrl(c.video_url)}
                      title={c.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                )}
                <div className="mt-4 flex items-center gap-3 text-xs text-[#5C6B61] flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    {c.scope === "national" ? <Globe className="w-3 h-3"/> : <Building2 className="w-3 h-3"/>}
                    {c.scope === "national" ? "National" : "Un hôpital"}
                  </span>
                  <span>·</span>
                  <span><strong>{c.sent_count}</strong> envoyés</span>
                  {c.hospitals_count > 1 && <><span>·</span><span>{c.hospitals_count} hôpitaux</span></>}
                  <span>·</span>
                  <span>{c.target_category || "Tous patients"}</span>
                  <span>·</span>
                  <span>{fmt(c.created_at)}</span>
                </div>
                {c.sent_by && <div className="mt-1 text-[10px] text-[#5C6B61] italic">Par {c.sent_by}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
